import React, { useState } from 'react';
import { BoltIcon } from '@patternfly/react-icons';
import { loginUser, rolesIncludeAdmin, LoginResponse } from '@app/utils/utils';
import { PURPLE_GRADIENT, colors, radii, shadows } from '@app/components/designTokens';

interface LoginComponentProps {
  triggerRefresh: (data: LoginResponse) => void;
}

export const LoginComponent: React.FunctionComponent<LoginComponentProps> = ({ triggerRefresh }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    setSubmitting(true);
    try {
      const data = await loginUser({ email, password });
      if (!data || !data.accessToken) {
        setErrorMessage('Invalid email or password');
        return;
      }
      // Gate the admin console at sign-in: only ROLE_ADMIN may enter, and we
      // never persist a token for a non-admin. Backend @PreAuthorize is the
      // real enforcement — this is the UX layer.
      if (!rolesIncludeAdmin(data.roles)) {
        setErrorMessage('This account is not authorized for the admin console.');
        return;
      }
      triggerRefresh(data);
    } catch (err: any) {
      setErrorMessage(err?.message || 'Invalid email or password');
    } finally {
      setSubmitting(false);
    }
  };

  const styles: { [k: string]: React.CSSProperties } = {
    page: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
      padding: 24
    },
    card: {
      width: '100%',
      maxWidth: 420,
      background: '#ffffff',
      borderRadius: radii.card,
      boxShadow: shadows.softCard,
      padding: 40
    },
    brand: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 },
    logoIcon: {
      width: 44,
      height: 44,
      borderRadius: 12,
      background: PURPLE_GRADIENT,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.boltGold,
      fontSize: 22,
      boxShadow: shadows.logoBadge
    },
    brandText: { fontSize: 22, fontWeight: 700, color: colors.title, letterSpacing: '-0.5px' },
    subtitle: { color: colors.subtle, fontSize: 14, margin: '4px 0 28px' },
    label: { display: 'block', fontSize: 14, fontWeight: 600, color: colors.body, marginBottom: 8 },
    input: {
      width: '100%',
      boxSizing: 'border-box',
      padding: '12px 14px',
      fontSize: 15,
      border: `1px solid ${colors.border}`,
      borderRadius: radii.input,
      marginBottom: 18,
      outline: 'none'
    },
    button: {
      width: '100%',
      padding: '13px 16px',
      fontSize: 15,
      fontWeight: 600,
      color: '#ffffff',
      background: submitting ? '#a78bfa' : PURPLE_GRADIENT,
      border: 'none',
      borderRadius: radii.button,
      cursor: submitting ? 'default' : 'pointer',
      boxShadow: shadows.primaryButton
    },
    error: {
      background: colors.dangerRing,
      color: colors.danger,
      borderRadius: 10,
      padding: '10px 14px',
      fontSize: 13,
      fontWeight: 500,
      marginBottom: 18
    },
    footer: { marginTop: 20, fontSize: 12, color: colors.placeholder, textAlign: 'center' }
  };

  return (
    <div style={styles.page}>
      <form style={styles.card} onSubmit={onSubmit}>
        <div style={styles.brand}>
          <div style={styles.logoIcon}>
            <BoltIcon />
          </div>
          <span style={styles.brandText}>AdVolt Admin</span>
        </div>
        <p style={styles.subtitle}>Platform operations console — admin access only.</p>

        {errorMessage && <div style={styles.error}>{errorMessage}</div>}

        <label style={styles.label} htmlFor="email">
          Email
        </label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          style={styles.input}
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
        />

        <label style={styles.label} htmlFor="password">
          Password
        </label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          style={styles.input}
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
        />

        <button type="submit" style={styles.button} disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <div style={styles.footer}>Authorized AdVolt operators only.</div>
      </form>
    </div>
  );
};
